<template>
  <div>
    <!-------------------------------------MOBILE CARD LIST-------------------------------------->
    <div class="d-md-none insert-parts-mobile-list mt-4 px-3 pb-4">
      <v-progress-linear
        v-if="loading"
        color="red"
        indeterminate
        class="mb-3"
      />

      <div
        v-for="(item, index) in parts"
        :key="item._id"
        class="insert-parts-mobile-card"
        :class="{ 'insert-parts-mobile-card--selected': index === activeRow }"
        @click="activeRow = index"
      >
        <div class="insert-parts-mobile-card__main">
          <v-checkbox
            :model-value="
              item.status !== 4 &&
              item.status !== 5 &&
              selected.some((s) => s._id === item._id)
            "
            :disabled="item.status === 4 || item.status === 5"
            color="primary"
            hide-details
            density="compact"
            class="insert-parts-mobile-card__checkbox flex-grow-0"
            @click.stop
            @update:model-value="toggleSelection(item)"
          />

          <div class="insert-parts-mobile-card__media">
            <button
              type="button"
              class="insert-parts-mobile-card__image-btn"
              @click.stop="showImages(item.images)"
            >
              <img
                :src="getPartImage(item)"
                :alt="getPartTitle(item)"
                class="insert-parts-mobile-card__image"
              />
            </button>
            <button
              type="button"
              class="insert-parts-mobile-card__qr-btn"
              @click.stop="
                openQrDialog({
                  id: item._id,
                  type: 'temporary-parts',
                  printed: item.printed,
                  car: item.car_id,
                })
              "
            >
              {{ item._id.slice(-6) }}
            </button>
          </div>

          <div class="insert-parts-mobile-card__info">
            <v-chip
              v-if="
                (role === 'LekoAdmin' || role === 'SuperAdmin') &&
                item.institution_id
              "
              size="x-small"
              class="insert-parts-mobile-card__institution mb-1"
            >
              {{ item.institution_id.title }}
            </v-chip>
            <div
              v-if="getPartCategoryLabel(item)"
              class="insert-parts-mobile-card__subtitle text-truncate"
            >
              {{ getPartCategoryLabel(item) }}
            </div>
            <div
              v-if="getPartInfoTitle(item)"
              class="insert-parts-mobile-card__title"
            >
              {{ getPartInfoTitle(item) }}
            </div>
            <button
              v-if="item.shipment_id && item.car_id"
              type="button"
              class="insert-parts-mobile-card__vehicle"
              @click.stop="viewCar(item.car_id)"
            >
              <v-icon size="16" class="insert-parts-shipment__icon"
                >mdi-car-outline</v-icon
              >
              <span>{{ getPartVehicleLabel(item) }}</span>
            </button>
            <div
              v-else
              class="insert-parts-mobile-card__vehicle insert-parts-mobile-card__vehicle--plain"
            >
              <v-icon
                size="16"
                color="grey"
                class="insert-parts-shipment__icon"
                >mdi-car-off</v-icon
              >
              <span>{{ getPartVehicleLabel(item) }}</span>
            </div>
            <div
              v-if="getPartPriceLabel(item)"
              class="insert-parts-mobile-card__price"
            >
              {{ getPartPriceLabel(item) }}
            </div>
            <button
              v-if="item.notes"
              type="button"
              class="insert-parts-mobile-card__notes"
              @click.stop="showNotes(item.notes)"
            >
              <span v-if="item.notes.length > 40">
                {{ item.notes.substring(0, 40) }}...
                <v-icon size="14" color="grey-darken-1">mdi-eye</v-icon>
              </span>
              <span v-else>{{ item.notes }}</span>
            </button>
            <!-- <div class="insert-parts-mobile-card__location text-truncate">
              {{ getPartLocationLabel(item) }}
            </div> -->
            <div class="text-truncate">{{ formatDate(getDate(item)) }}</div>
          </div>
        </div>

        <div class="insert-parts-mobile-card__actions">
          <v-chip
            v-if="getStatusLabel(item.status)"
            size="small"
            :color="getStatusColor(item.status)"
            variant="tonal"
            class="insert-parts-mobile-card__status mr-1 text-caption font-weight-medium"
            @click.stop="movement(item)"
            @mouseover="
              item.status === 3 ? activateSnackbar(item.internal_notes) : null
            "
          >
            {{ getStatusLabel(item.status) }}
          </v-chip>
          <v-spacer />
          <v-btn icon variant="text" size="small" @click.stop="actions(item)">
            <v-icon color="grey-darken-2">mdi-compare-horizontal</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            size="small"
            @click.stop="editPart(item, false, index)"
          >
            <v-icon color="grey-darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="share(item)">
            <v-icon color="grey-darken-2">mdi-share-variant-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            size="small"
            @click.stop="deletePart(item._id, item.kit_id)"
          >
            <v-icon color="grey-darken-2">mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <v-alert
        v-if="!loading && parts.length < 1"
        type="info"
        border="start"
        color="red-lighten-2"
        variant="tonal"
        class="mt-2"
      >
        No data available.
      </v-alert>
    </div>

    <!----------------------------------------DATA TABLE---------------------------------------->
    <div class="insert-parts-table-shell">
      <StickyHScroll>
        <v-data-table-server
          ref="dataTable"
          v-model="selected"
          v-model:options="pagination"
          :items="parts"
          :items-length="totalItems"
          :headers="
            role === 'LekoAdmin' || role === 'SuperAdmin'
              ? headersAdmin
              : headers
          "
          :items-per-page="pagination.itemsPerPage"
          :items-per-page-options="[
            { value: 5, title: '5' },
            { value: 10, title: '10' },
            { value: 25, title: '25' },
            { value: 50, title: '50' },
          ]"
          :loading="loading"
          items-per-page-text="Rows per page"
          item-key="_id"
          return-object
          show-select
          class="elevation-1 mt-6 nowrap-headers insert-parts-server-table"
          @update:options="updatePagination"
        >
        <template #progress>
          <v-progress-linear color="red" indeterminate />
        </template>

        <template #group="props">
          <v-row align-center row>
            <span v-if="props.groupName" class="ml-5 mr-2">{{
              $t("insert_parts.kit_qr")
            }}</span>
            <span v-if="props.groupName" class="ma-1">
              <qr-code :size="50" :text="props.groupName" />
            </span>
          </v-row>
        </template>
        <!----------------------------------------MAIN TABLE----------------------------------------->
        <template #item="{ item, index }">
          <tr
            :class="{ tableRowColorSelected: index === activeRow }"
            class="tableRowColor"
            @click="activeRow = index"
          >
            <!-- SELECTION -->
            <td>
              <v-row align="center" justify="center">
                <v-checkbox
                  :model-value="
                    item.status !== 4 &&
                    item.status !== 5 &&
                    selected.some((s) => s._id === item._id)
                  "
                  :disabled="item.status === 4 || item.status === 5"
                  color="primary"
                  hide-details
                  dense
                  @change="toggleSelection(item)"
                />
              </v-row>
            </td>

            <!-- QR Code -->
            <td class="ma-1 pt-1 pb-1">
              <v-row
                class="pointer ma-1 text-center justify-center d-block"
                @click="
                  openQrDialog({
                    id: item._id,
                    type: 'temporary-parts',
                    printed: item.printed,
                    car: item.car_id,
                  })
                "
              >
                <qr-code :size="60" :text="item._id" />
                <h4>{{ item._id.slice(-6) }}</h4>
              </v-row>
            </td>

            <!-- ACTIONS -->
            <td class="text-center">
              <v-row
                no-gutters
                align="center"
                justify="center"
                class="d-flex flex-nowrap"
              >
                <v-btn icon variant="text" @click.stop="actions(item)">
                  <v-icon color="grey-darken-2">mdi-compare-horizontal</v-icon>
                </v-btn>
                <v-btn
                  icon
                  variant="text"
                  @click.stop="editPart(item, false, index)"
                >
                  <v-icon color="grey-darken-2">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon variant="text" class="ma-0" @click="share(item)">
                  <v-icon color="grey-darken-2"
                    >mdi-share-variant-outline</v-icon
                  >
                </v-btn>
                <v-btn
                  icon
                  variant="text"
                  @click.stop="deletePart(item._id, item.kit_id)"
                >
                  <v-icon color="grey-darken-2">mdi-delete</v-icon>
                </v-btn>
              </v-row>
            </td>

            <!-- INSTITUTION -->
            <td
              v-if="
                (role === 'LekoAdmin' || role === 'SuperAdmin') &&
                item.institution_id
              "
            >
              <v-chip>
                {{ item.institution_id.title }}
              </v-chip>
            </td>
            <td v-else-if="role === 'LekoAdmin' || role === 'SuperAdmin'"></td>

            <!-- USER -->
            <td
              v-if="
                (role === 'LekoAdmin' || role === 'SuperAdmin') && item.user_id
              "
            >
              {{ item.user_id.name }}
            </td>
            <td v-else-if="role === 'LekoAdmin' || role === 'SuperAdmin'"></td>

            <!-- PRA -->
            <td class="text-center">{{ item.pra }}</td>

            <!-- BRAND -->
            <td class="text-center">
              {{
                item.brand && item.brand.toLowerCase().includes("error")
                  ? ""
                  : item.brand
              }}
            </td>

            <!-- EBAY CODE -->
            <td class="text-center">{{ item.ebayCode }}</td>

            <!-- NOTES -->
            <td>
              <v-row
                align="center"
                justify="center"
                class="pointer"
                @click="showNotes(item.notes)"
              >
                <span
                  v-if="item.notes && item.notes.length > 40"
                  style="min-width: 125px !important"
                >
                  {{ item.notes.substring(0, 40) }}...<v-icon>mdi-eye</v-icon>
                </span>
                <span v-else>{{ item.notes }}</span>
              </v-row>
            </td>

            <!-- CLASSIFICATION -->
            <td class="text-center">
              <span>
                {{
                  item.classificationWithAI &&
                  item.classificationWithAI.toLowerCase().includes("error")
                    ? "Image Not Analyzed"
                    : item.classificationWithAI
                }}
              </span>
              <span
                v-if="
                  role === 'SuperAdmin' &&
                  item.classificationWithAIProbability !== undefined &&
                  item.classificationWithAIProbability !== null
                "
                class="text-caption text-medium-emphasis ml-1"
              >
                ({{ formatProbability(item.classificationWithAIProbability) }})
              </span>
            </td>

            <!-- IMAGE -->
            <td>
              <v-row align="center" justify="center">
                <img
                  :src="
                    item.images &&
                    item.images.length > 0 &&
                    item.images[0].thumb
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.images[0].thumb}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="pointer"
                  height="75"
                  @click="showImages(item.images)"
                />
              </v-row>
            </td>

            <!-- CONDITION NEW -->
            <td class="text-center">
              <v-icon v-if="item.isNewPart">mdi-check-circle-outline</v-icon>
            </td>

            <!-- OEM CODE -->
            <td>{{ item.infoCar.oemCode }}</td>

            <!-- PRICE/COST -->
            <td class="text-no-wrap">
              <span v-if="item.price">
                {{ $t("parts.table.price") }}: {{ item.price }}
                {{ item.currency }}
              </span>
              <br />
              <span v-if="item.cost">
                {{ $t("parts.table.cost") }}: {{ item.cost }}
                {{ item.currency }}
              </span>
            </td>

            <!-- LOCATION -->
            <td v-if="item.shelf_id && item.shelf_data">
              {{ item.shelf_data.shelf.room_name || "" }} -
              {{ item.shelf_data.shelf.location_code }}:
              {{ item.shelf_data.shelf.x }}, {{ item.shelf_data.shelf.y }}
              <div class="text-no-wrap">
                <span class="caption">Mag: </span>
                <span class="font-weight-bold caption">{{
                  item.shelf_data.repo
                }}</span>
              </div>
            </td>
            <td v-else>-</td>

            <!-- SHIPMENT -->
            <td v-if="item.shipment_id" style="min-width: 150px !important">
              <a
                v-if="item.car_id"
                class="insert-parts-shipment"
                @click="viewCar(item.car_id)"
              >
                <v-icon size="16" class="insert-parts-shipment__icon"
                  >mdi-car-outline</v-icon
                >
                <span class="insert-parts-shipment__name">{{
                  item.shipment_id.name
                }}</span>
              </a>
              <span v-else class="insert-parts-shipment">
                <v-icon
                  size="16"
                  color="grey"
                  class="insert-parts-shipment__icon"
                  >mdi-car-off</v-icon
                >
                <span class="insert-parts-shipment__name">{{
                  item.shipment_id.name
                }}</span>
              </span>
            </td>
            <td v-else></td>

            <!-- QUALITY + STATUS -->
            <td style="min-width: 190px !important" class="text-center">
              <div class="pt-1 d-flex flex-column align-center">
                <span
                  >{{ item.warranty_id.return_days / 86400 }}
                  {{ $t("warranties.days") }}</span
                >
                <v-rating
                  v-if="item.quality"
                  :model-value="item.quality.rating"
                  color="red"
                  background-color="red-darken-4"
                  readonly
                  :size="16"
                />
                <v-rating
                  v-else
                  :model-value="0"
                  color="red"
                  background-color="red-darken-4"
                  readonly
                  :size="16"
                />
              </div>
              <div class="text-truncate">{{ formatDate(getDate(item)) }}</div>
              <v-chip
                v-if="item.status === 1"
                variant="outlined"
                color="primary"
                label
                class="mb-1"
                @click.stop="movement(item)"
              >
                {{ $t("parts.status.available") }}
              </v-chip>
              <v-chip
                v-else-if="item.status === 2"
                variant="outlined"
                color="yellow-darken-2"
                label
                class="mb-1"
                @click.stop="movement(item)"
              >
                {{ $t("parts.status.waiting_approval") }}
              </v-chip>
              <v-chip
                v-else-if="item.status === 3"
                variant="outlined"
                color="yellow-darken-2"
                label
                class="mb-1"
                @click.stop="movement(item)"
                @mouseover="activateSnackbar(item.internal_notes)"
              >
                {{ $t("parts.status.review_needed") }}
              </v-chip>
              <v-chip
                v-else-if="item.status === 4"
                variant="outlined"
                color="error"
                label
                class="mb-1"
                @click.stop="movement(item)"
              >
                {{ $t("parts.status.refused") }}
              </v-chip>
              <v-chip
                v-else-if="item.status === 5"
                variant="outlined"
                color="success"
                label
                class="mb-1"
                @click.stop="movement(item)"
              >
                {{ $t("parts.status.approved") }}
              </v-chip>
            </td>
          </tr>
          <tr v-if="parts && parts.length < 1">
            <td :colspan="headers.length" class="text-center pa-4">
              <v-alert type="info" border="left" color="red lighten-2" text>
                No data available.
              </v-alert>
            </td>
          </tr>
        </template>
      </v-data-table-server>
    </StickyHScroll>
    </div>
    <v-dialog v-model="confirmDialog" width="auto">
      <v-card>
        <v-card-title class="pb-0 mb-0">
          <v-alert class="mb-0" :alert.value="true" type="error">
            {{ $t("delete_alert") }}
          </v-alert>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="grey" @click="cancelDelete">{{
            $t("close")
          }}</v-btn>
          <v-btn color="red" text :loading="loading" @click="confirmDelete">{{
            $t("delete")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <generalAlert ref="generalAlert" />
  </div>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator"; // eslint-disable-line import/extensions
import getRequest from "../../api/getRequest";
import deleteRequest from "../../api/deleteRequest";
import generalAlert from "@/components/generalAlert.vue";

let lastRequestId = 0;
export default {
  components: {
    "qr-code": VueQrCode,
    generalAlert,
  },
  props: ["search", "selected_items"], // eslint-disable-line vue/require-prop-types
  data: () => ({
    activeRow: null,
    expand: false,
    loading: false,
    loadingAdditional: false,
    headers: [
      {
        title: "QR Code",
        sortable: false,
        key: "_id",
      },
      {
        title: "Actions",
        sortable: false,
        key: "actions",
      },
      {
        title: "Pra",
        sortable: true,
        key: "pra",
      },
      {
        title: "Brand",
        sortable: true,
        key: "brand",
      },
      {
        title: "Ebay Code",
        sortable: false,
        key: "ebayCode",
      },
      {
        title: "Notes",
        sortable: false,
        key: "notes",
      },
      {
        title: "Classification With AI",
        sortable: false,
        key: "classificationWithAI",
      },
      {
        title: "Images",
        sortable: false,
        key: "images",
      },
      {
        title: "New",
        sortable: false,
        key: "isNewPart",
      },
      // {
      //   text: 'Audio',
      //   sortable: false,
      //   value: 'audio',
      // },
      {
        title: "OEM Code",
        sortable: true,
        key: "infoCar.oemCode",
      },
      {
        title: "Price",
        sortable: true,
        key: "price",
      },
      {
        title: "Location",
        sortable: false,
        key: "location",
      },
      {
        title: "Shipment Name",
        sortable: false,
        key: "shipment_id",
      },
      // {
      //   text: 'Warranty ID',
      //   sortable: false,
      //   value: 'warranty_id',
      // },
      // {
      //   text: 'Quality',
      //   sortable: true,
      //   value: 'quality',
      // },
      {
        title: "Date Created",
        sortable: true,
        key: "date_created",
      },
      // {
      //   text: 'Status',
      //   sortable: true,
      //   value: 'status',
      // },
    ],
    headersAdmin: [
      {
        title: "QR Code",
        sortable: false,
        key: "_id",
      },
      {
        title: "Actions",
        sortable: false,
        key: "actions",
      },
      {
        text: "Institution",
        sortable: false,
        key: "institution",
      },
      {
        title: "User",
        sortable: false,
        key: "user",
      },
      {
        title: "Pra",
        sortable: true,
        key: "pra",
      },
      {
        title: "Brand",
        sortable: true,
        key: "brand",
      },
      {
        title: "Ebay Code",
        sortable: false,
        key: "ebayCode",
      },
      {
        title: "Notes",
        sortable: false,
        key: "notes",
      },
      {
        title: "Classification With AI",
        sortable: false,
        key: "classificationWithAi",
      },
      {
        title: "Images",
        sortable: false,
        key: "images",
      },
      {
        title: "New",
        sortable: false,
        key: "isNewPart",
      },
      // {
      //   text: 'Audio',
      //   sortable: false,
      //   value: 'audio',
      // },
      {
        title: "OEM Code",
        sortable: true,
        key: "infoCar.oemCode",
      },
      {
        title: "Price",
        sortable: true,
        key: "price",
      },
      {
        title: "Location",
        sortable: false,
        key: "location",
      },
      {
        title: "Shipment Name",
        sortable: false,
        key: "shipment_id",
      },
      // {
      //   text: 'Warranty ID',
      //   sortable: false,
      //   value: 'warranty_id',
      // },
      // {
      //   text: 'Quality',
      //   sortable: true,
      //   value: 'quality',
      // },
      {
        title: "Date Created",
        sortable: true,
        key: "date_created",
      },
      // {
      //   text: 'Status',
      //   sortable: true,
      //   value: 'status',
      // },
    ],
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["date_created"],
      sortBy: [{ key: "date_created", order: "desc" }],
      sortDesc: [true],
    },
    parts: [],
    totalItems: 0,
    additionalParts: [],
    selected: [],
    confirmDialog: false,
    confirmId: null,
    confirmKitId: null,
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
    role() {
      return this.$store.getters.role;
    },
    searchObject() {
      return this.$props.search;
    },
    selectedStatusItems() {
      return this.$props.selected_items;
    },
  },
  watch: {
    selected: {
      handler(data) {
        const validItems = data.filter(
          (item) => item.status !== 4 && item.status !== 5,
        );
        this.$emit("approve", {
          value: validItems.length > 0,
          items: validItems,
        });
      },
      deep: true,
    },

    searchObject: {
      handler(value) {
        if (value.type === null) {
          this.getParts(this.selectedStatusItems);
        } else if (value.type === "regularSearch") {
          this.regularSearch(value.data, this.selectedStatusItems);
        } else {
          //
        }
      },
      deep: true,
      immediate: true,
    },

    selectedStatusItems: {
      handler(value) {
        if (this.searchObject.type === null) {
          this.getParts(value);
        }
      },
      deep: true,
    },

    parts(value) {
      this.$emit("items", value);
    },
  },
  mounted() {
    this.getLocale();
    if (this.$store.getters.hasUnpaidInvoices) {
      this.$nextTick(() => {
        if (this.$refs.generalAlert) {
          this.$refs.generalAlert.open({
            title: this.$t("unpaid_invoice_alert_title"),
            message: this.$t("unpaid_invoice_alert_message"),
            type: "info",
          });
        }
      });
    }
  },
  created() {
    this.getParts(this.selectedStatusItems);
  },
  methods: {
    uncheck() {
      this.selected = [];
    },
    formatProbability(value) {
      if (value === undefined || value === null || value === "") return "";
      const num = Number(value);
      if (!Number.isFinite(num)) return "";
      const percent = num <= 1 ? num * 100 : num;
      return `${Math.round(percent)}%`;
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    getLocale() {
      this.headers[0].title = this.$t("parts.table.qr_code");
      this.headers[1].title = this.$t("parts.table.actions");
      this.headers[2].title = this.$t("parts.table.pra");
      this.headers[3].title = this.$t("parts.table.brand");
      this.headers[4].title = this.$t("parts.table.ebay_code");
      this.headers[5].title = this.$t("parts.table.notes");
      this.headers[6].title = this.$t("parts.table.classification_with_ai");
      this.headers[7].title = this.$t("parts.table.images");
      this.headers[8].title = this.$t("parts.table.new");
      // this.headers[8].text = this.$t('parts.table.audio');
      this.headers[9].title = this.$t("parts.table.oem_code");
      this.headers[10].title = this.$t("parts.table.price");
      this.headers[11].title = this.$t("parts.table.location");
      this.headers[12].title = this.$t("parts.table.shipment");
      // this.headers[10].text = this.$t('parts.table.warranty');
      // this.headers[10].text = this.$t('parts.table.quality');
      this.headers[13].title = this.$t("parts.movement_history.table.date");

      // this.headers[10].text = this.$t('parts.table.status');
      this.headersAdmin[0].title = this.$t("parts.table.qr_code");
      this.headersAdmin[1].title = this.$t("parts.table.actions");
      this.headersAdmin[2].title = this.$t("parts.table.institution");
      this.headersAdmin[3].title = this.$t("parts.table.user");
      this.headersAdmin[4].title = this.$t("parts.table.pra");
      this.headersAdmin[5].title = this.$t("parts.table.brand");
      this.headersAdmin[6].title = this.$t("parts.table.ebay_code");
      this.headersAdmin[7].title = this.$t("parts.table.notes");
      this.headersAdmin[8].title = this.$t(
        "parts.table.classification_with_ai",
      );
      this.headersAdmin[9].title = this.$t("parts.table.images");
      this.headersAdmin[10].title = this.$t("parts.table.new");
      // this.headersAdmin[10].text = this.$t('parts.table.audio');
      this.headersAdmin[11].title = this.$t("parts.table.oem_code");
      this.headersAdmin[12].title = this.$t("parts.table.price");
      this.headersAdmin[13].title = this.$t("parts.table.location");
      this.headersAdmin[14].title = this.$t("parts.table.shipment");
      // this.headersAdmin[12].text = this.$t('parts.table.warranty');
      // this.headersAdmin[13].text = this.$t('parts.table.quality');
      this.headersAdmin[15].title = this.$t(
        "parts.movement_history.table.date",
      );
      // this.headersAdmin[13].text = this.$t('parts.table.status');
    },
    getParts(statusArray) {
      const requestId = ++lastRequestId;
      this.selected = [];
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };

      getRequest(
        "/temporary-part/insert/datatable/",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&status=${statusArray}`,
      )
        .then((response) => {
          if (requestId !== lastRequestId) return;
          this.$scrollToTop();
          this.totalItems = response.count;
          this.parts = response.temporaryParts;
          this.loading = false;
          // this.closeExpandedRow();
        })
        .catch(() => {
          this.loading = false;
        });
    },
    regularSearch(data, statusArray) {
      const requestId = ++lastRequestId;
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        `/temporary-part/insert/search/${data.type}`,
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&status=${statusArray}&search=${data.searchField}`,
      )
        .then((response) => {
          if (requestId !== lastRequestId) return;
          this.$scrollToTop();
          this.totalItems = response.count;
          this.parts = response.temporaryParts;
          this.loading = false;
          this.closeExpandedRow();
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getAdditionalParts(kitId, expanded) {
      if (expanded && kitId) {
        this.loadingAdditional = true;
        getRequest("/temporary-part/additional", `kit_id=${kitId}`)
          .then((response) => {
            this.additionalParts = response.temporaryParts;
            this.loadingAdditional = false;
          })
          .catch(() => {
            this.additionalParts = [];
            this.loadingAdditional = false;
          });
      } else {
        this.additionalParts = [];
      }
    },
    activateSnackbar(data) {
      this.$emit("activate-snackbar", data);
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    showImages(images) {
      this.$emit("images", { type: "string", images });
    },
    actions(data) {
      this.$emit("actions", data);
    },
    editPart(data, kit) {
      this.$emit("edit", data, kit);
    },
    deletePart(id, kitId) {
      this.confirmId = id;
      this.confirmKitId = kitId;
      this.confirmDialog = true;
    },
    confirmDelete() {
      this.loading = true;
      deleteRequest(
        "/temporary-part/insert",
        `id=${this.confirmId}&user_id=${this.$store.getters.userId}&institution_id=${this.$store.getters.institutionId}`,
      )
        .then(() => {
          this.loading = false;
          this.$emit("delete");
          this.confirmDialog = false;
          this.confirmId = null;
          this.confirmKitId = null;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    cancelDelete() {
      this.confirmDialog = false;
      this.confirmId = null;
      this.confirmKitId = null;
    },
    async updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "date_created", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      // await this.getParts(this.selectedStatusItems);
      // Fetch new data
      if (this.searchObject.type === null) {
        await this.getParts(this.selectedStatusItems);
      } else if (this.searchObject.type === "regularSearch") {
        await this.regularSearch(
          this.searchObject.data,
          this.selectedStatusItems,
        );
      } else {
        //
      }
    },
    closeExpandedRow() {
      /**
       * TODO: make this smarter so it doesn't throw an error when there is no kit part expanded.
       */
      this.$set(
        this.$refs.dataTable.expanded,
        this.parts[this.activeRow]._id,
        false,
      );
    },
    viewCar(carId) {
      this.$emit("view-car", { carId });
    },
    showNotes(notes) {
      this.$emit("show-notes", notes);
    },
    movement(data) {
      this.$emit("movement", data);
    },
    getDate(part) {
      const history = part.movement_history;
      if (!history?.length) return "";
      return history[history.length - 1].date || "";
    },
    toggleSelection(item) {
      const index = this.selected.findIndex((s) => s._id === item._id);
      if (index === -1) {
        this.selected.push(item);
      } else {
        this.selected.splice(index, 1);
      }
    },
    share(data) {
      this.$emit("share", data);
    },
    getPartImage(item) {
      if (item.images?.length > 0 && item.images[0].thumb) {
        return `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.images[0].thumb}`;
      }
      return "https://portal.lekotech.com/logo_small.png";
    },
    getPartInfoTitle(item) {
      const lang = this.$store.getters.language;
      return item?.infoCar?.description?.descriptionId?.title?.[lang] || "";
    },
    getPartCategoryLabel(item) {
      const lang = this.$store.getters.language;
      return item?.infoCar?.description?.descriptionId?.category?.[lang] || "";
    },
    getPartPriceLabel(item) {
      const parts = [];

      if (item.price) {
        parts.push(
          `${this.$t("parts.table.price")}: ${item.price} ${item.currency || ""}`.trim(),
        );
      }
      if (item.cost) {
        parts.push(
          `${this.$t("parts.table.cost")}: ${item.cost} ${item.currency || ""}`.trim(),
        );
      }

      return parts.join(" · ");
    },
    getPartTitle(item) {
      const lang = this.$store.getters.language;
      const infoTitle =
        item?.infoCar?.description?.descriptionId?.title?.[lang];
      if (infoTitle) return infoTitle;

      const ai = item.classificationWithAI;
      if (ai && !ai.toLowerCase().includes("error")) return ai;

      return item.ebayCode || item.notes || "—";
    },
    getPartSubtitle(item) {
      const lang = this.$store.getters.language;
      const category =
        item?.infoCar?.description?.descriptionId?.category?.[lang];
      const idSuffix = item._id ? item._id.slice(-6) : "";

      if (category && idSuffix) return `${category} · ${idSuffix}`;
      if (item.ebayCode && idSuffix) return `${item.ebayCode} · ${idSuffix}`;
      return idSuffix || "—";
    },
    getPartVehicleLabel(item) {
      const brand =
        item.brand && !item.brand.toLowerCase().includes("error")
          ? item.brand
          : "";
      const shipment = item.shipment_id?.name || "";
      return [brand, shipment].filter(Boolean).join(" ") || "—";
    },
    getPartLocationLabel(item) {
      if (!item.shelf_id || !item.shelf_data?.shelf) return "—";

      const { shelf } = item.shelf_data;
      const slot =
        shelf.location_code ||
        [shelf.x, shelf.y].filter(Boolean).join("-") ||
        "";
      const repo = item.shelf_data.repo
        ? `Mag. ${item.shelf_data.repo}`
        : "";

      return [slot, repo].filter(Boolean).join(" · ") || "—";
    },
    getStatusLabel(status) {
      if (status === 1) return this.$t("parts.status.available");
      if (status === 2) return this.$t("parts.status.waiting_approval");
      if (status === 3) return this.$t("parts.status.review_needed");
      if (status === 4) return this.$t("parts.status.refused");
      if (status === 5) return this.$t("parts.status.approved");
      return "";
    },
    getStatusColor(status) {
      if (status === 1) return "primary";
      if (status === 2 || status === 3) return "yellow-darken-2";
      if (status === 4) return "error";
      if (status === 5) return "success";
      return "grey";
    },
  },
};
</script>

<style scoped>
.insert-parts-mobile-list {
  background: #f5f5f5;
}

.insert-parts-mobile-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.insert-parts-mobile-card--selected {
  background: #fafafa;
  border-color: #bdbdbd;
}

.insert-parts-mobile-card__main {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 12px 8px;
}

.insert-parts-mobile-card__checkbox {
  margin-top: 2px;
}

.insert-parts-mobile-card__media {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.insert-parts-mobile-card__image-btn {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.insert-parts-mobile-card__image {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  background: #212121;
  display: block;
}

.insert-parts-mobile-card__qr-btn {
  min-width: 72px;
  margin-top: 2px;
  padding: 4px 8px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  color: #424242;
  cursor: pointer;
  text-align: center;
}

.insert-parts-mobile-card__qr-btn:active {
  background: #f5f5f5;
}

.insert-parts-mobile-card__info {
  min-width: 0;
  flex: 1;
}

.insert-parts-mobile-card__title {
  margin-top: 2px;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.3;
  color: #212121;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
}

.insert-parts-mobile-card__subtitle {
  font-size: 0.78rem;
  color: #757575;
}

.insert-parts-shipment {
  display: inline-flex;
  align-items: flex-start;
}

.insert-parts-shipment__icon {
  flex-shrink: 0;
  margin-right: 4px;
  margin-top: 1px;
}

.insert-parts-shipment__name {
  white-space: pre-line;
}

.insert-parts-mobile-card__vehicle {
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.35;
  color: #1565c0;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  width: 100%;
}

.insert-parts-mobile-card__vehicle--plain {
  cursor: default;
  color: #1565c0;
}

.insert-parts-mobile-card__price {
  margin-top: 4px;
  font-size: 0.78rem;
  color: #424242;
  line-height: 1.35;
}

.insert-parts-mobile-card__notes {
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.78rem;
  color: #616161;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  width: 100%;
}

.insert-parts-mobile-card__location {
  margin-top: 4px;
  font-size: 0.78rem;
  color: #757575;
}

.insert-parts-mobile-card__date {
  margin-top: 4px;
  font-size: 0.78rem;
  color: #616161;
}

.insert-parts-mobile-card__institution {
  max-width: 100%;
}

.insert-parts-mobile-card__institution :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.insert-parts-mobile-card__status {
  flex-shrink: 0;
  max-width: calc(100% - 140px);
}

.insert-parts-mobile-card__status :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insert-parts-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  padding: 2px 4px 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

@media (max-width: 959.98px) {
  .insert-parts-table-shell :deep(.v-table__wrapper) {
    display: none !important;
  }

  .insert-parts-table-shell :deep(.global-sticky-hscroll) {
    display: none !important;
  }

  .insert-parts-table-shell :deep(.insert-parts-server-table) {
    margin-top: 0 !important;
    box-shadow: none;
    background: transparent;
  }

  .insert-parts-table-shell :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}

@media (min-width: 960px) {
  .insert-parts-mobile-list {
    display: none !important;
  }
}
</style>
